/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/**
 * Resolves cascading field data dependencies (lookups & variations).
 *
 * - `getFieldsDataDependency` filters fields whose `dataDependency`
 *   chain has been affected by a change in `changedFields`.
 * - `fetchFieldsDataDependency` resolves those dependent option lists
 *   via `getLookupsAndVariations` and writes them back into `steps`.
 */

import { getLookupsAndVariations } from "src/api";
import { differenceBetweenTwoObjects } from "@lib/sharedUtils";
import { flatten } from "@lib/shared/lodash";
import { Field, Step } from "@lib/shared/types";
import { FieldsData, InitState } from "../types";

interface UseFieldDataDependencyParams {
    steps: Step[];
    setSteps: (s: Step[]) => void;
    prevFieldsState: FieldsData | {} | null;
    fieldsState: FieldsData;
    nextState: () => Promise<InitState>;
}

export const useFieldDataDependency = ({
    steps,
    setSteps,
    prevFieldsState,
    fieldsState,
    nextState,
}: UseFieldDataDependencyParams) => {
    const getFieldsDataDependency = (
        {
            checkChangedFields,
            changedFields,
        }: {
            checkChangedFields?: boolean;
            changedFields: FieldsData;
        } = { checkChangedFields: true, changedFields: {} },
    ) => {
        const allFields = flatten(steps.map((step) => step.fields));
        const changedFieldsState = differenceBetweenTwoObjects(
            prevFieldsState,
            { ...fieldsState, ...changedFields },
        ) as FieldsData;

        const fieldsWithDataDependencyAndDependentOn = allFields.filter(
            (f) =>
                f.dataDependency &&
                f.dataDependency.dependency &&
                f.dataDependency?.dependency?.some((dependencyItem: any) =>
                    checkChangedFields
                        ? !!changedFieldsState[dependencyItem.fieldCodeName]
                        : true,
                ),
        );

        return {
            fieldsWithDataDependencyAndDependentOn,
        };
    };

    const fetchFieldsDataDependency = async (
        fieldsWithDataDependencyAndDependentOn: Field[],
    ) => {
        const { fieldsState: newFieldsState } = await nextState();
        for await (const field of fieldsWithDataDependencyAndDependentOn) {
            const dependentOnFields: {
                dependentOnType: "lookups" | "variations";
                id: number | undefined;
            }[] = [];
            const tempSteps = [...steps];
            let fieldIndex = 0;

            //Get step index for the field
            const stepIndex = steps.findIndex((step) =>
                step.fields.some((f: any, index: number) => {
                    if (f.codeName === field.codeName) {
                        fieldIndex = index;
                        return true;
                    }
                    return false;
                }),
            );

            // Fetch fields data if its depend on another field
            if (
                field.dataDependency?.dependency?.every((dependencyItem) => {
                    const allFields = flatten(steps.map((step) => step.fields));
                    const dependentOnField = allFields.find(
                        (f: any) => f.codeName === dependencyItem.fieldCodeName,
                    );

                    const dependentUponFieldData =
                        newFieldsState[dependentOnField?.codeName as string];

                    dependentOnFields.push({
                        dependentOnType: dependencyItem.dependentOn,
                        id: dependentOnField?.defaultValues?.options.find(
                            (options: any) =>
                                options.value === dependentUponFieldData.value,
                        )?.id,
                    });

                    return (
                        dependentUponFieldData.value &&
                        dependentUponFieldData.valid
                    );
                })
            ) {
                const { ordering, type, source } = field.dataDependency;
                const variationsIds = dependentOnFields.filter(
                    (i) => i.dependentOnType === "variations",
                );

                // should fire loading for field and clear all option before getting the response
                const updatedSteps = [...steps];
                updatedSteps[stepIndex].fields[fieldIndex] = {
                    ...updatedSteps[stepIndex].fields[fieldIndex],
                    fetchingFieldData: true,
                };
                updatedSteps[stepIndex].fields[fieldIndex].defaultValues = {
                    options: [],
                };
                setSteps(updatedSteps);

                const { data, error } = await getLookupsAndVariations({
                    source,
                    queries: {
                        orderBy: ordering?.field,
                        orderByDirection: ordering?.direction,
                        type,
                        lookupsId: dependentOnFields.find(
                            (i) => i.dependentOnType === "lookups",
                        )?.id,
                        variationsIds: variationsIds.length
                            ? variationsIds.map((i) => i.id).join(",")
                            : undefined,
                    },
                });

                if (data && !error) {
                    // reset fetchingFieldData to false to hide loading inside select
                    tempSteps[stepIndex].fields[fieldIndex] = {
                        ...tempSteps[stepIndex].fields[fieldIndex],
                        fetchingFieldData: false,
                    };
                    tempSteps[stepIndex].fields[fieldIndex].defaultValues = {
                        options: data,
                    };
                    setSteps(tempSteps);
                }
            } else {
                // reset fetchingFieldData to false to hide loading inside select
                tempSteps[stepIndex].fields[fieldIndex] = {
                    ...tempSteps[stepIndex].fields[fieldIndex],
                    fetchingFieldData: false,
                };
                tempSteps[stepIndex].fields[fieldIndex].defaultValues = {
                    options: [],
                };

                setSteps(tempSteps);
            }
        } // end for
    };

    return {
        getFieldsDataDependency,
        fetchFieldsDataDependency,
    };
};
